import { z } from 'zod'
import { CompanyIdSchema, idSchemaV1, isId, UserIdSchema } from '../ids'
import { FeatureRuleDataSchema } from '../feature/model'
import { TinyUserSchema } from '../entity'
import { AuditPropsSchema } from '../common'
import { ArchivableSchema } from '../archive'

export const QuestionIdSchema = idSchemaV1('Question')
export type QuestionId = z.infer<typeof QuestionIdSchema>
export const isQuestionId = isId(QuestionIdSchema)

export const QuestionChoiceTypeSchema = z.enum(['MultipleChoice', 'RatingScale', 'YesNo'])
export type QuestionChoiceType = z.infer<typeof QuestionChoiceTypeSchema>

export const QuestionRecurrenceSchema = z.enum(['week', 'month', 'quarter', 'year'])
export type QuestionRecurrence = z.infer<typeof QuestionRecurrenceSchema>

export const ASQ_CATEGORIES = [
  'ManagerEngagement',
  'CompanyCulture',
  'Product',
  'CareerDevelopment',
  'TeamDynamics',
] as const
export const AsqCategorySchema = z.enum(ASQ_CATEGORIES)
export type AsqCategory = z.infer<typeof AsqCategorySchema>

const QuestionSkipBehaviorSchema = z.enum(['skip', 'snooze'])
export type QuestionSkipBehavior = z.infer<typeof QuestionSkipBehaviorSchema>

export const QuestionStoreReviewSchema = z.object({
  choiceIndices: z.number().int().array().meta({
    description:
      'Answers that mark the user as a store review candidate, matched against the stored answer choiceIndex. On rating questions these are the ratings themselves (1-5); on other choice types they are 0-based choice indexes',
  }),
  delayDays: z.number().nonnegative().default(3).meta({
    description: 'Days the qualifying answer must be old before the user becomes a candidate',
  }),
})
export type QuestionStoreReview = z.infer<typeof QuestionStoreReviewSchema>

const QuestionFollowUpSchema = z.object({
  questionId: QuestionIdSchema.meta({ description: 'The question to ask right after this one is answered' }),
  triggerIndices: z.number().array().optional().meta({
    description:
      'Choice indexes that trigger this follow-up. Omit it to trigger on any choice no other follow-up lists',
  }),
})

export const UnsavedQuestionSchema = z.object({
  startDate: z.number().optional().meta({
    description: 'When users start being asked, in UTC milliseconds. A question without one is never asked',
  }),
  endDate: z.number().optional().meta({ description: 'When users stop being asked, in UTC milliseconds' }),
  choiceType: QuestionChoiceTypeSchema.meta({
    description: '`YesNo` has two choices and `RatingScale` five; `MultipleChoice` uses `choices`',
  }),
  choices: z
    .array(z.string())
    .optional()
    .meta({ description: 'Answer labels. Required unless `choiceType` is `YesNo`' }),
  text: z.string().meta({ description: 'The question shown to users' }),
  askToShareWithManager: z.boolean().optional(),
  category: AsqCategorySchema.optional(),
  recurs: QuestionRecurrenceSchema.optional().meta({
    description:
      'Recurrence period (Pacific time) of a recurring question. A user is asked again once one full period has passed since their own latest answer or skip (a monthly question answered Oct 31 is asked again Nov 30). Open questions are asked in order of the end of their current calendar period',
  }),
  rules: z.array(FeatureRuleDataSchema).optional().meta({
    description:
      'Ordered targeting rules for who is asked, evaluated top to bottom; the first matching rule decides. Empty means everyone who can see the question',
  }),
  confirmManager: z.boolean().optional().meta({
    description: 'Asks the user to confirm or pick their manager after choosing an answer',
  }),
  skipBehavior: QuestionSkipBehaviorSchema.default('skip')
    .optional()
    .meta({ description: 'Determines whether to skip the question or snooze it when the user chooses to skip.' }),
  followUps: QuestionFollowUpSchema.array().optional(),
  storeReview: QuestionStoreReviewSchema.optional().meta({
    description:
      'Flags answers that make the user a store review candidate. Terros staff only, on a Terros-owned rating question',
  }),
})
export type UnsavedQuestion = z.infer<typeof UnsavedQuestionSchema>

export const QuestionDataSchema = z.object({
  ...AuditPropsSchema.shape,
  ...ArchivableSchema.shape,
  ...UnsavedQuestionSchema.shape,
  questionId: QuestionIdSchema.meta({ description: 'The question' }),
  companyId: CompanyIdSchema.meta({ description: 'The company that owns the question' }),
})
export type QuestionData = z.infer<typeof QuestionDataSchema>

export function getAnswerCount(question?: QuestionData): number {
  if (!question) return 0
  if (question.choiceType === 'RatingScale') return 5
  if (question.choiceType === 'YesNo') return 2
  return question.choices?.length ?? 0
}

export const UnsavedQuestionAnswerSchema = z.object({
  questionId: QuestionIdSchema,
  choiceIndex: z.number().optional(),
  shareWithManager: z.boolean().optional(),
})
export type UnsavedQuestionAnswer = z.infer<typeof UnsavedQuestionAnswerSchema>

export const QuestionAnswerDataSchema = z.object({
  ...AuditPropsSchema.shape,
  ...UnsavedQuestionAnswerSchema.shape,
  user: TinyUserSchema,
  answerDate: z.number(),
  managerId: UserIdSchema.optional(),
})
export type QuestionAnswerData = z.infer<typeof QuestionAnswerDataSchema>
